/**
 * Firebase Cloud Messaging (FCM) Push Notification Service
 * Uses Firebase compat SDK loaded via CDN (global `firebase` object)
 */
import { CONFIG, AppState } from '../config.js';
import { apiRequest } from './api.js';
import { getDeviceId } from './device-id.js';
import { showToast } from '../utils/ui.js';

let messagingInstance = null;
let initialized = false;
let currentToken = null;

const FCM_TOKEN_KEY = 'ss_fcm_token';

/**
 * Initialize Firebase app and messaging.
 * Safe to call multiple times.
 */
async function initFirebaseMessaging() {
    if (initialized) return messagingInstance;

    if (!CONFIG.FIREBASE || !CONFIG.FIREBASE.apiKey) {
        console.warn('[FCM] Firebase config not set');
        return null;
    }

    const { vapidKey, ...firebaseConfig } = CONFIG.FIREBASE;

    if (!firebase.apps.length) {
        firebase.initializeApp(firebaseConfig);
    }

    messagingInstance = firebase.messaging();
    initialized = true;
    return messagingInstance;
}

/**
 * Request notification permission and subscribe to push
 */
export async function subscribeToPush() {
    const msg = await initFirebaseMessaging();
    if (!msg) return null;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        console.log('[FCM] Permission denied');
        return null;
    }

    const swReg = await navigator.serviceWorker.getRegistration('/');

    const token = await msg.getToken({
        vapidKey: CONFIG.FIREBASE.vapidKey,
        serviceWorkerRegistration: swReg
    });

    if (!token) {
        console.error('[FCM] Failed to get token');
        return null;
    }

    // Send to backend
    await apiRequest('/push/subscribe', {
        method: 'POST',
        body: JSON.stringify({
            fcm_token: token,
            device_id: getDeviceId(),
            user_agent: navigator.userAgent
        })
    });

    currentToken = token;
    localStorage.setItem(FCM_TOKEN_KEY, token);
    return token;
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush() {
    const msg = await initFirebaseMessaging();
    if (!msg) return;

    await apiRequest('/push/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({
            fcm_token: currentToken,
            device_id: getDeviceId()
        })
    });

    await msg.deleteToken();
    currentToken = null;
    localStorage.removeItem(FCM_TOKEN_KEY);
}

/**
 * Refresh token if changed
 */
export async function refreshTokenIfNeeded() {
    try {
        const msg = await initFirebaseMessaging();
        if (!msg) return;

        const swReg = await navigator.serviceWorker.getRegistration('/');
        const token = await msg.getToken({
            vapidKey: CONFIG.FIREBASE.vapidKey,
            serviceWorkerRegistration: swReg
        });

        if (!token) return;

        const storedToken = localStorage.getItem(FCM_TOKEN_KEY);
        if (storedToken && storedToken !== token) {
            console.log('[FCM] Token changed, updating');
            await apiRequest('/push/update', {
                method: 'PUT',
                body: JSON.stringify({
                    fcm_token: token,
                    device_id: getDeviceId(),
                    old_fcm_token: storedToken
                })
            });
            localStorage.setItem(FCM_TOKEN_KEY, token);
        }

        currentToken = token;
    } catch (error) {
        console.error('[FCM] Token refresh failed:', error);
    }
}

/**
 * Listen for foreground messages
 */
export function onForegroundMessage(callback) {
    if (!messagingInstance) return;
    messagingInstance.onMessage((payload) => {
        callback(payload.notification || payload.data);
    });
}

/**
 * Listen for push notifications forwarded from the service worker
 * when the app is in the foreground.
 */
export function setupForegroundPushListener() {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'PUSH_NOTIFICATION') {
            const { title, body } = event.data.payload || {};
            showToast(body || title || 'New notification', 'info', true);
        }
    });
}

/**
 * Check if push is supported
 */
export function isPushSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator;
}

/**
 * Request push permission (soft ask)
 */
export async function requestPushPermission() {
    if (!isPushSupported()) return false;

    if (Notification.permission === 'granted') {
        await subscribeToPush();
        return true;
    }

    if (Notification.permission === 'denied') {
        return false;
    }

    // Default: ask
    const result = await subscribeToPush();
    return !!result;
}
